import { PageController } from '@client/components/_core/layout/PageController';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { match } from 'ts-pattern';
import { type GovernanceConfig, GovernanceConfigForm } from './GovernanceConfigForm';

export const Route = createFileRoute('/console/$orgId/configuration/compliance/mspr/governance-config/add')({
    component: AddGovernanceConfig,
    staticData: {
        presentation: 'overlay',
    },
});

function AddGovernanceConfig() {
    const { trpc, trpcUtils } = useTrpc();
    const [open, setOpen] = useState(true);
    const orgId = useOrgId();
    const navigate = useNavigate();
    const [data, setData] = useState<GovernanceConfig>({
        fieldName: '',
    });
    const { mutate: createGovernanceConfig, isPending: isCreating } =
        trpc.mspr.governanceConfig.createGovernanceConfig.useMutation();

    const handleSubmit = (values: GovernanceConfig) => {
        setData(values);
        const key = 'create_governance';
        message.loading({ content: 'Creating…', key });
        createGovernanceConfig(
            {
                fieldName: values.fieldName ?? '',
            },
            {
                onSuccess: () => {
                    message.success({ content: 'Created', key });
                    trpcUtils.mspr.governanceConfig.listGovernanceConfigs.invalidate();
                    setOpen(false);
                },
                onError: (error) => {
                    message.error(error.message || 'Failed to create governance configuration');
                },
            },
        );
    };

    return (
        <PageController route={Route}>
            <GovernanceConfigForm
                open={open}
                mode="add"
                data={data}
                state={match({ isCreating })
                    .with({ isCreating: true }, () => 'submitting' as const)
                    .otherwise(() => 'ready' as const)}
                onSubmit={handleSubmit}
                onCloseFinished={() => {
                    navigate({
                        to: '/console/$orgId/configuration/compliance/mspr/governance-config',
                        params: { orgId },
                    });
                }}
            />
        </PageController>
    );
}
